import { useCallback, useMemo, useRef, useState } from "react";

import {
  IScreenKeyboardOptions,
  ScreenKeyboardVariant,
} from "@components/screenKeyboard/screenKeyboard.types";

export const useScreenKeyboardState = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>("");
  const [variant, setVariant] = useState<ScreenKeyboardVariant>("default");
  const [pattern, setPattern] = useState<RegExp | null>(null);
  const [shiftFirst, setShiftFirst] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);
  const onApplyRef = useRef<((value: string) => void) | null>(null);

  const openKeyboard = useCallback(
    (
      initialValue: string,
      onApply: (value: string) => void,
      options?: IScreenKeyboardOptions
    ) => {
      onApplyRef.current = onApply;
      setValue(initialValue);
      setVariant(options?.variant ?? "default");
      setPattern(options?.pattern ?? null);
      setShiftFirst(options?.shiftFirst ?? false);
      setSuggestions(options?.suggestions ?? null);
      setIsOpen(true);
    },
    []
  );

  const closeKeyboard = useCallback(() => {
    setIsOpen(false);
    onApplyRef.current = null;
  }, []);

  const applyKeyboard = useCallback(() => {
    onApplyRef.current?.(value);
    setIsOpen(false);
    onApplyRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return useMemo(
    () => ({
      isOpen,
      value,
      variant,
      pattern,
      shiftFirst,
      suggestions,
      setValue,
      openKeyboard,
      closeKeyboard,
      applyKeyboard,
    }),
    [
      isOpen,
      value,
      variant,
      pattern,
      shiftFirst,
      suggestions,
      openKeyboard,
      closeKeyboard,
      applyKeyboard,
    ]
  );
};
